import React from 'react';

import { styled } from 'styled-components';

import ShareLinks from 'components/Article/ShareLinks';
import { ArticleDate } from 'components/ArticleDate';
import InnerHtml from 'components/InnerHtml/InnerHtml';
import Link from 'components/Link';
import ModularBlocks from 'components/ModularBlocks';
import Disclaimer from 'components/ModularBlocks/Disclaimer';
import { SmallPrint } from 'components/ModularBlocks/Disclaimer/styled/DisclaimerBody';
import { MediaQuery } from 'components/StyledComponents/MediaQueryRendering';

import { AuthorI } from 'interfaces/content-stack/author';
import { BlogPostI } from 'interfaces/content-stack/blogPost';
import { FetchPriority } from 'interfaces/html';

import { autoOptimizeImage } from 'utils/functions';
import { getFullUrl } from 'utils/linkResolver';

const ArticleContentContainer = styled.div`
  padding: 3rem;
  background-color: ${(props) => props.theme.colors.white};

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    padding: 2rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 1.5rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    padding: 0rem;
  }

  ${SmallPrint} {
    padding: 0;
  }
`;

const ArticleTitle = styled.h1`
  font-family: ${(props) => props.theme.fonts.fontFamilySecondary};
  font-size: 2rem;
  text-transform: uppercase;
  margin-top: 0;
  margin-bottom: 0;
  font-weight: 500;
  line-height: 1.4;
  margin-top: 0.5rem;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    font-size: 1.5rem;
  }
`;

const ArticleExcerpt = styled.div`
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.5;
  margin-top: 1.25rem;
`;

const ArticleImageWrapper = styled.div`
  margin-top: 3rem;
  position: relative;
  display: flex;
  flex-direction: column;

  img {
    width: 100%;
    aspect-ratio: 3 / 2;
    object-fit: cover;
  }
`;

const ImageCaption = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  margin-top: 0.5rem;
  align-self: flex-end;
`;

const AuthorByline = styled.div`
  margin-top: 2rem;
  padding-bottom: 2rem;
  display: flex;
  gap: 0.75rem;
  border-bottom: solid 1px ${(props) => props.theme.colors.secondaryGray2};
`;

const AuthorDetails = styled.div`
  display: flex;
  font-weight: 500;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: start;
    gap: 0;
  }
`;

const AuthorImage = styled.img`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  max-width: 100%;
  display: inline-block;
`;

const AuthorName = styled(Link)`
  color: inherit;
  font-weight: 700;
`;

const BodyContent = styled.div`
  margin-top: 3rem;
  margin-bottom: 5rem;
  box-sizing: border-box;

  h2 {
    font-size: 1.5rem;
    padding-top: 1.5rem;
    padding-bottom: 1rem;
  }

  h3 {
    font-size: 1.25rem;
  }

  h4 {
    font-size: 1.15rem;
  }

  h5 {
    font-size: 1.1rem;
  }

  h6 {
    font-size: 1rem;
  }

  p {
    margin-bottom: 1rem;
  }

  table {
    font-family: ${(props) => props.theme.fonts.default};
    margin-bottom: 1.5rem;
    width: 100%;

    &.content-padding-extra {
      td,
      th {
        padding: 1rem 1.5rem;

        @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
          padding: 0.75rem;
        }
      }

      td {
        @media (max-width: ${(props) => props.theme.breakpoints.md}) {
          padding-left: 1rem;
          padding-right: 1rem;
        }

        @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
          padding: 0.75rem;
        }
      }

      th {
        @media (max-width: ${(props) => props.theme.breakpoints.xl}) {
          padding-top: 0.5rem;
          padding-bottom: 0.5rem;
        }

        @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
          padding-left: 1.5rem;
          padding-right: 1.5rem;
        }

        @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
          padding-left: 0.75rem;
          padding-right: 0.75rem;
        }

        @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
          padding-top: 0.25rem;
          padding-bottom: 0.25rem;
        }
      }
    }

    &.text-weight-semibold {
      td,
      th {
        font-weight: 600;
      }
    }
  }

  table tr {
    &:nth-child(odd) td {
      background-color: ${(props) => props.theme.colors.secondaryGray5};
    }
  }

  table th {
    text-transform: uppercase;
    width: auto;
    font-weight: 600;
    font-size: 0.75rem;
    padding: 0.75rem 0.5rem;

    @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
      padding: 0.5rem;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
      font-size: 0.66rem;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
      text-align: center;
    }
  }

  table td {
    vertical-align: top;
    font-size: 1rem;
    font-weight: 500;
    padding: 0.75rem;

    &:not(:last-child) {
      border-right: 0.0625rem solid ${(props) => props.theme.colors.borderLightGray2};
    }

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
      font-size: 0.8125rem;
      padding-left: 0.5rem;
      padding-right: 0.5rem;
    }
  }

  .accordion {
    display: none;
  }

  .image-component-root {
    position: relative;

    img {
      background-position: 50%;
      background-repeat: no-repeat;
      background-size: cover;
      display: block;
      height: 100%;
      left: 0;
      object-fit: cover;
      position: absolute;
      top: 0;
      width: 100%;
    }
  }

  iframe[src*='youtube'] {
    aspect-ratio: 1.77777778;
    height: auto;
    width: 100%;
  }
`;

const Review = styled.div`
  border-bottom: solid 1px ${(props) => props.theme.colors.secondaryGray2};
  padding-bottom: 2rem;
  display: flex;
  margin-top: 2.25rem;
  align-items: center;
  text-align: center;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: start;
  }
`;

const ReviewedBy = styled.div`
  font-weight: 600;
  margin-right: 0.75rem;
`;

const Reviewer = styled(Link)`
  font-weight: 600;
  color: inherit;
  margin-right: 0.75rem;
`;

const BylineDivider = styled.span`
  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    display: none;
  }
`;

const ReviewerTitle = styled.div`
  @media (min-width: ${(props) => props.theme.breakpoints.sm}) {
    margin-left: 0.75rem;
  }
`;

interface ArticleContentProps {
  data: BlogPostI;
  siteUrl: string;
}

const ArticleContent: React.FC<ArticleContentProps> = ({ data, siteUrl }) => {
  const { postDate, title, metadata, bodyContent, pageBody, breadcrumb } = data;
  const {
    excerpt,
    thumbnailImage,
    thumbnailImageCaption,
    thumbnailAltText,
    disclaimer,
    articleReviewer,
    articleAuthor,
  } = metadata;
  const contentContributorUrl = (contentContributor: AuthorI[]) => {
    const metaUrl = contentContributor?.[0]?.url;
    const urlPrefix = contentContributor?.[0]?.breadcrumb?.[0]?.url;
    const contributorUrl = urlPrefix ? urlPrefix + metaUrl : metaUrl;

    return contributorUrl?.replaceAll('//', '/');
  };

  return (
    <ArticleContentContainer>
      {postDate && <ArticleDate date={postDate} />}
      <ArticleTitle data-elastic-name={'page_title'}>{title}</ArticleTitle>
      <ArticleExcerpt>
        <InnerHtml richText={excerpt} />
      </ArticleExcerpt>
      <ArticleImageWrapper>
        <img
          src={autoOptimizeImage(thumbnailImage?.url, true, '820')}
          alt={thumbnailAltText}
          fetchPriority={FetchPriority.HIGH}
        />
        <ImageCaption>{thumbnailImageCaption}</ImageCaption>
      </ArticleImageWrapper>
      {articleAuthor && articleAuthor?.length > 0 && (
        <AuthorByline>
          <AuthorImage
            src={autoOptimizeImage(articleAuthor[0]?.authorThumbnail?.url, true, '32')}
            alt={`Picture of ${articleAuthor[0]?.title}`}
          />
          <AuthorDetails>
            <AuthorName to={contentContributorUrl(articleAuthor)}>{articleAuthor[0]?.title}</AuthorName>
            <BylineDivider>|</BylineDivider>
            <div>
              {articleAuthor[0]?.authorRole}
              {articleAuthor[0]?.authorRole && articleAuthor[0]?.authorLocation ? `, ` : ''}
              {articleAuthor[0]?.authorLocation}
            </div>
          </AuthorDetails>
        </AuthorByline>
      )}
      <BodyContent>
        <InnerHtml richText={bodyContent} />
      </BodyContent>
      {pageBody && <ModularBlocks modularBlocks={pageBody} />}
      <MediaQuery $mobile>
        <ShareLinks
          shareUrl={getFullUrl({
            siteUrl,
            pageSegment: data.url,
            breadcrumbSegment: breadcrumb?.[0]?.url,
          })}
        />
      </MediaQuery>
      {articleReviewer && articleReviewer?.length > 0 && (
        <Review>
          <ReviewedBy>Reviewed by:</ReviewedBy>
          <Reviewer to={contentContributorUrl(articleReviewer)}>{articleReviewer[0]?.title}</Reviewer>
          <BylineDivider>|</BylineDivider>
          <ReviewerTitle>
            {articleReviewer[0]?.authorRole}
            {articleReviewer[0]?.authorRole && articleReviewer[0]?.authorLocation && `, `}
            {articleReviewer[0]?.authorLocation}
          </ReviewerTitle>
        </Review>
      )}
      {disclaimer && <Disclaimer fontSize='0.75rem' data={{ reference: disclaimer }} />}
    </ArticleContentContainer>
  );
};

export default ArticleContent;
